import { Box, SelectChangeEvent, Switch, alpha } from "@mui/material";
import { ChangeEvent, FormEvent, useState } from "react";
import SelectBox from "../../components/selectBox";
import { SearchForm } from "../../components/common/SearchForm";
import {
  RadioOption,
  TableTitle,
} from "../../components/mobileSize/TableTitle";
import { DotLoader } from "../../components/dotLoader";
import { MobileInfo, Mobility, SystemSetting } from "../../types/types";
import { getEventOptions, getMobileOptions } from "../../utils/constants";
import { Button } from "../../components/common/Button";
import theme from "../../theme";
import Spinner from "../../components/spinner";
import { MobileTable } from "../../components/mobileSize/MobileTable";
import styled from "@emotion/styled";
import {
  Battery0,
  Battery1,
  Battery2,
  Battery3,
  Battery4,
  Battery5,
  GrayClassAndBoardIcon,
  GrayMapIcon,
  TrashCanIcon,
} from "../../assets/icons/icons";
import MobilityRegisterModal from "../../components/mobilityRegisterModal";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import {
  copyToClipboard,
  formatDate,
  isOlderThan24Hours,
} from "../../utils/util";
import { useSnackbar } from "notistack";
import { FetchParams } from "./mobileList";
import Calendars from "../../components/calendars";
import { SelectBoxGroupId } from "../../components/SelectBoxGroupId";
import { useI18n } from "../../locales";

const SORT_LIST: RadioOption[] = [
  { label: `기본 순`, value: `` },
  { label: `기기명 순`, value: `mobility_name` },
  { label: `기기종류 순`, value: `mobility_type` },
  { label: `소유자 순`, value: `name` },
  { label: `운행여부 순`, value: `status` },
  { label: `최근운행일시 순`, value: `gps_date` },
  { label: `단말기시리얼번호 순`, value: `device_pid` },
  { label: `농기계배터리전압 순`, value: `board_input_volt` },
  { label: `단발배터리상태 순`, value: `backup_volt` },
  { label: `점검모드 순`, value: `test_mode` },
];

type Prop = {
  data?: Mobility;
  isLoading?: boolean;
  systemSetting: SystemSetting | null;
  sort?: string;
  setSort?: React.Dispatch<React.SetStateAction<string | undefined>>;
  onClickBtn: ({
    startDate,
    endDate,
    searchKey,
    eventCode,
    mobFilter,
  }: FetchParams) => void;
  setRealMobFilter: any;
  onClickMapIcon: (el: MobileInfo) => void;
  onClickTestMode: (pid: string, mode: 0 | 1) => void;
  onClickDetailIcon: (seq: number) => void;
  onClickDeleteIcon: (el: MobileInfo) => void;
};

export const MobileMobileList = ({
  data,
  isLoading,
  systemSetting,
  sort,
  setSort,
  onClickBtn,
  setRealMobFilter,
  onClickMapIcon,
  onClickTestMode,
  onClickDetailIcon,
  onClickDeleteIcon,
}: Prop) => {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  const [openFilter, setOpenFilter] = useState(false);
  const [mobFilterOpen, setMobFilterOpen] = useState(false);
  const [input, setInput] = useState("");
  const [mobFilter, setMobFilter] = useState(-1); // 가짜
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [groupSub, setGroupSub] = useState("all"); // 하위그룹코드(화면용)
  const [eventCode, setEventCode] = useState(0);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const mobHandleClose = () => {
    setMobFilterOpen(false);
  };

  const mobHandleOpen = () => {
    setMobFilterOpen(true);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onClickBtn({
      startDate,
      endDate,
      mobFilter,
      eventCode,
      searchKey: input,
      groupId: groupSub,
    });
    setRealMobFilter(mobFilter);
  };

  const mobHandleChange = (event: SelectChangeEvent) => {
    setMobFilter(Number(event.target.value));
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.currentTarget.value);
  };

  const onClickCopy = (text: string) => {
    copyToClipboard(text);
    enqueueSnackbar(`클립보드에 복사되었습니다.`, {
      variant: "success",
    });
  };

  const handleClose = () => {
    setFilterOpen(false);
  };

  const handleOpen = () => {
    setFilterOpen(true);
  };

  const handleChange = (event: SelectChangeEvent<typeof eventCode>) => {
    setEventCode(Number(event.target.value));
  };

  return (
    <>
      {/* 필터 섹션 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: openFilter ? "auto" : "0px",
          maxHeight: openFilter ? "100vh" : "0px",
          overflow: openFilter ? "visible" : "hidden",
          gap: "6px",
          paddingBottom: openFilter ? "16px" : "0px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: `8px`,
            alignItems: "center",
          }}
        >
          <SelectBox
            open={mobFilterOpen}
            filter={mobFilter}
            handleChange={mobHandleChange}
            handleClose={mobHandleClose}
            handleOpen={mobHandleOpen}
            title="기기 종류 선택"
            menuItems={getMobileOptions(t)}
            width={140}
            mr="0px"
            srLabel="기기 종류 선택"
          />
          <SelectBox
            open={filterOpen}
            handleChange={handleChange}
            handleOpen={handleOpen}
            handleClose={handleClose}
            filter={eventCode}
            menuItems={getEventOptions(t)}
            title="감지 종류 선택"
            width={140}
            mr="0px"
            srLabel="감지 종류 선택"
          />
        </Box>

        <Calendars
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isSeparate={false}
          width={140}
          justifyContent="start"
          gap="8px"
        ></Calendars>

        <Box
          sx={{
            display: "flex",
            gap: `8px`,
            alignItems: "center",
          }}
        >
          {/* 하위 그룹 조회 */}
          <SelectBoxGroupId
            groupSub={groupSub}
            setGroupSub={setGroupSub}
            width={140}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: `4px`,
            alignItems: "center",
          }}
        >
          <SearchForm
            onSubmit={onSubmit}
            placeholder="검색"
            onChange={onChange}
            inputValue={input}
            setInputValue={setInput}
            width="140px"
            gap="6px"
            bottom="0"
            marginR="0"
          />
          <Button
            bgColor={theme.color.aquamarine_500}
            hoverColor={theme.color.aquamarine_600}
            height="30px"
            isDisabled={!systemSetting?.create}
            width="96px"
            onClick={() => setOpenRegisterModal(true)}
            paddingX="0px"
          >
            기기 등록하기
          </Button>
        </Box>
      </Box>

      {/* 테이블 타이틀 섹션 */}
      <TableTitle
        title={
          isLoading ? (
            <div style={{ height: "15px" }}>
              <DotLoader height="6px" />
            </div>
          ) : (
            `조회된 기기 - ${data?.total_items ?? 0} 건`
          )
        }
        sort={sort}
        setSort={setSort}
        setOpenFilter={setOpenFilter}
        sortList={SORT_LIST}
      />

      {/* 테이블 섹션 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          flexGrow: 1,
        }}
      >
        {isLoading && <Spinner />}
        {data &&
          data.list.length > 0 &&
          data.list.map((el) => (
            <MobileTable
              key={el.seq}
              defaultInfoGap="2px"
              defaultInfo={
                <>
                  <TextWrapper>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "16px",
                        width: "70%",
                      }}
                    >
                      {/* 운행 여부 */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                        }}
                      >
                        <StatusCircle
                          isLive={el.status === "운행중" ? true : false}
                        />
                        <Text $width="100%">{el.status ?? "미운행"}</Text>
                      </Box>
                      {/* 기기명 */}
                      <CustomTooltip title={el.mobility_name} active>
                        <Text
                          $width="60%"
                          onClick={() => {
                            onClickCopy(el.mobility_name);
                          }}
                        >
                          {el.mobility_name}
                        </Text>
                      </CustomTooltip>
                    </Box>
                    {/* 기기종류 */}
                    <CustomTooltip title={el.mobility_type} active>
                      <Text $width="30%" style={{ textAlign: "right" }}>
                        {el.mobility_type}
                      </Text>
                    </CustomTooltip>
                  </TextWrapper>
                  <TextWrapper>
                    {/* 단말시리얼번호 */}
                    <Text
                      onClick={() => {
                        onClickCopy(el.device_pid);
                      }}
                    >
                      {el.device_pid}
                    </Text>
                    {/* 농기계 배터리 전압 */}
                    <Text
                      style={{
                        color:
                          Number(el.board_input_volt) < 12
                            ? theme.color.red_500
                            : theme.color.black,
                      }}
                    >
                      {el.board_input_volt + "V"}
                    </Text>
                    {/* 단말배터리상태 */}
                    <CustomTooltip title={el.backup_volt} active>
                      <BattertyIconWrapper>
                        {Number(el.backup_volt) > 3.91 ? (
                          <Battery5 />
                        ) : Number(el.backup_volt) > 3.81 ? (
                          <Battery4 />
                        ) : Number(el.backup_volt) > 3.61 ? (
                          <Battery3 />
                        ) : Number(el.backup_volt) > 3.41 ? (
                          <Battery2 />
                        ) : Number(el.backup_volt) > 3.11 ? (
                          <Battery1 />
                        ) : (
                          <Battery0 />
                        )}
                      </BattertyIconWrapper>
                    </CustomTooltip>
                  </TextWrapper>
                  <TextWrapper>
                    {/* 최근운행일시 */}

                    <Text
                      $width="35%"
                      style={{
                        color: isOlderThan24Hours(el.gps_date)
                          ? theme.color.red_500
                          : theme.color.neutral_600,
                      }}
                    >
                      {!el.gps_date
                        ? "주행기록없음"
                        : formatDate(el.gps_date, "yyyy-MM-dd HH:mm")}
                    </Text>

                    {/* 소유자 */}
                    <CustomTooltip title={el.name} active>
                      <Text
                        $width="50%"
                        style={{ textAlign: "right" }}
                        onClick={() => {
                          onClickCopy(el.name);
                        }}
                      >
                        {el.name}
                      </Text>
                    </CustomTooltip>
                  </TextWrapper>
                </>
              }
              icons={
                // 지도에서 보기 아이콘
                <CustomTooltip title={"지도에서 보기"} active>
                  <IconWrapper onClick={() => onClickMapIcon(el)}>
                    <GrayMapIcon />
                  </IconWrapper>
                </CustomTooltip>
              }
              detailInfo={
                <DetailWrapper>
                  {/* 점검모드 */}
                  <Switch
                    checked={el.test_mode === 0 ? false : true}
                    onClick={() =>
                      onClickTestMode(el.device_pid, el.test_mode)
                    }
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: theme.statusColor.MAIN,
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.statusColor.MAIN,
                            0.3
                          ),
                        },
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: theme.statusColor.MAIN,
                        },
                    }}
                  />
                  {/* 기기정보 상세보기 아이콘 */}
                  <CustomTooltip title={"기기정보 상세보기"} active>
                    <IconWrapper
                      onClick={() => onClickDetailIcon(el.seq)}
                      $wrapperWH="28px"
                      $iconWH="16px"
                    >
                      <GrayClassAndBoardIcon />
                    </IconWrapper>
                  </CustomTooltip>
                  {/* 기기 삭제하기 아이콘 */}
                  <CustomTooltip title={"기기 삭제하기"} active>
                    <IconWrapper
                      onClick={() => {
                        if (!systemSetting?.delete) return;
                        onClickDeleteIcon(el);
                      }}
                      $wrapperWH="28px"
                      $iconWH="14px"
                    >
                      <TrashCanIcon />
                    </IconWrapper>
                  </CustomTooltip>
                </DetailWrapper>
              }
            />
          ))}
        {!isLoading && data && data.list.length === 0 && (
          <NoDataMassage>조회된 데이터가 없습니다.</NoDataMassage>
        )}
        {openRegisterModal && (
          <MobilityRegisterModal
            open={openRegisterModal}
            onClose={() => setOpenRegisterModal(false)}
          />
        )}
      </Box>
    </>
  );
};

const TextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100vw - 32px - 32px - 38px);
`;

const Text = styled.div<{ $fontSize?: string; $width?: string }>`
  font-size: ${({ $fontSize }) =>
    $fontSize ? $fontSize : theme.fontSize.FONT_2};
  flex: ${({ $width }) => ($width ? `0 1 ${$width}` : "1")};
  width: ${({ $width }) => ($width ? $width : "30%")};
  max-width: ${({ $width }) => ($width ? $width : "30%")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusCircle = styled.span<{ isLive: boolean }>`
  display: block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ isLive }) =>
    isLive ? `${theme.color.green_500}` : `${theme.color.neutral_400}`};
`;

const IconWrapper = styled.button<{
  $wrapperWH?: string;
  $iconWH?: string;
}>`
  width: ${({ $wrapperWH }) => ($wrapperWH ? $wrapperWH : `36px`)};
  height: ${({ $wrapperWH }) => ($wrapperWH ? $wrapperWH : `36px`)};
  border-radius: 50%;
  background: ${theme.color.neutral_200};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: ${({ $iconWH }) => ($iconWH ? $iconWH : `18px`)};
    height: ${({ $iconWH }) => ($iconWH ? $iconWH : `18px`)};
  }
`;

const BattertyIconWrapper = styled.div`
  svg {
    width: 32px;
    height: 32px;
  }
`;

const NoDataMassage = styled.div`
  text-align: center;
  color: ${theme.color.neutral_500};
  padding-top: 200px;
`;

const DetailWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 16px;

  .MuiSwitch-root {
    padding: 4px 4px;
    width: 37px;
    height: 20px;
  }

  .MuiButtonBase-root {
    height: 20px;
  }
`;
